import type { RouteLanguageCode } from '../utils/languageRouting';
import japaneseAnnouncement from './award-announcements/jp';
import koreanAnnouncement from './award-announcements/kr';
import thaiAnnouncement from './award-announcements/th';
import vietnameseAnnouncement from './award-announcements/vn';
import germanAnnouncement from './award-announcements/de';
import frenchAnnouncement from './award-announcements/fr';
import italianAnnouncement from './award-announcements/it';
import spanishAnnouncement from './award-announcements/es';
import uyghurAnnouncement from './award-announcements/ug';

export type AwardAnnouncementLanguage = RouteLanguageCode;

type AwardAnnouncementSection = {
  heading: string;
  paragraphs: string[];
};

export type AwardAnnouncementContent = {
  lang: string;
  eyebrow: string;
  title: string;
  date: string;
  location: string;
  award: string;
  summary: string;
  introduction: string[];
  sections: AwardAnnouncementSection[];
  readMore: string;
  readLess: string;
};

export const HOME_AWARD_ANNOUNCEMENT_ID = 'asia-pacific-gold-trading-leading-brand-2026';

// The announcement is temporary and automatically stops appearing after this Hong Kong time.
export const HOME_AWARD_ANNOUNCEMENT_EXPIRES_AT = '2026-11-01T00:00:00+08:00';

export const homeAwardAnnouncement: Record<
  AwardAnnouncementLanguage,
  AwardAnnouncementContent
> = {
  hk: {
    lang: 'zh-Hant',
    eyebrow: '公司新聞',
    title: 'GLG 2026 年度榮膺「亞太黃金交易領軍品牌」',
    date: '2026 年 9 月 22 日',
    location: '香港',
    award: '亞太黃金交易領軍品牌',
    summary:
      '金利寶環球有限公司於香港舉行的全球金融產業博覽會中獲頒「亞太黃金交易領軍品牌」獎項，肯定公司在貴金屬交易服務、專業能力與品牌建設方面的持續投入。',
    introduction: [
      '2026 年 9 月 22 日，全球金融產業博覽會在香港舉行。來自金融交易、金融科技、資產管理及相關服務領域的多家機構、專家與專業人士齊聚現場，共同交流全球金融市場發展趨勢、金融科技創新及行業生態建設。',
      '金利寶環球有限公司（GOLD LABEL GLOBAL LIMITED）作為參展機構亮相本屆博覽會，並在大會頒獎環節榮獲「亞太黃金交易領軍品牌」獎項。',
      '此次參展與獲獎，不僅是金利寶環球品牌發展進程中的重要節點，也代表行業對金利寶在貴金屬領域持續投入、專業服務及品牌建設成果的進一步關注與認可。',
    ],
    sections: [
      {
        heading: '榮譽加冕，見證金利寶在貴金屬領域的專業實力',
        paragraphs: [
          '在金融市場不斷走向全球化、專業化與科技化的背景下，投資者對交易環境、服務能力、風險管理及品牌專業性的要求持續提高。',
          '作為專注於貴金屬交易服務的金融品牌，金利寶環球一直圍繞貴金屬市場深化專業能力，從交易服務、技術環境到客戶體驗與品牌建設，不斷完善整體服務體系。',
          '此次獲得「亞太黃金交易領軍品牌」榮譽，是對金利寶環球在黃金及貴金屬交易領域持續發展的階段性認可。對金利寶而言，獎項不僅是一份榮譽，更代表一份責任。',
          '每一次行業認可，背後都離不開對專業標準的長期堅持。面對不斷變化的全球金融市場，金利寶將繼續以穩健、專業和長期主義為核心，持續提升服務能力，以更成熟的品牌形象面向市場與客戶。',
        ],
      },
      {
        heading: '匯聚行業力量，連接全球金融生態',
        paragraphs: [
          '本屆全球金融產業博覽會匯聚多家金融機構、行業專家、渠道夥伴及專業投資者，為金融行業提供交流趨勢、連接資源與分享創新成果的平台。',
          '展會期間，金利寶環球透過品牌展位展示、現場交流及行業互動，與來自不同市場的金融從業者及合作夥伴深入溝通。',
          '從行業趨勢到市場需求，從金融科技發展到貴金屬交易服務升級，現場交流讓金利寶進一步了解全球金融市場的變化，也為品牌未來連接更多行業資源及深化市場合作帶來新契機。',
          '對金利寶而言，參加行業展會不僅是一次品牌展示；更重要的是透過與市場、行業及合作夥伴持續交流，讓品牌真正融入全球金融產業生態，並在不斷變化的市場環境中保持敏銳、開放與專業。',
        ],
      },
      {
        heading: '深耕貴金屬交易，持續構建專業服務體系',
        paragraphs: [
          '金利寶環球有限公司立足香港，持續聚焦黃金、白銀等貴金屬交易領域。',
          '作為香港金銀業貿易場金集團成員之一，金利寶持續完善自身貴金屬業務體系，並在交易環境、技術支援、客戶服務與市場教育等方向持續投入。',
          '依託香港國際金融中心的市場優勢，金利寶希望進一步連接亞太及全球市場，為不同地區的客戶提供更專業、高效的貴金屬交易服務體驗。',
          '與此同時，金利寶亦持續關注金融科技發展趨勢，探索數碼工具、金融科技及智能技術的應用，為未來業務發展構建更完善的技術基礎與服務能力。',
          '從產品與服務到品牌與市場，從交易體驗到金融教育與行業交流，金利寶正逐步構建更完整的品牌生態。',
        ],
      },
      {
        heading: '載譽前行，讓專業成為長期價值',
        paragraphs: [
          '榮譽是一個階段的認可，也是下一段旅程的開始。此次亮相全球金融產業博覽會並榮獲「亞太黃金交易領軍品牌」，進一步堅定了金利寶持續深耕貴金屬領域的發展方向。',
          '未來，金利寶環球將繼續立足香港、連接全球，堅持以專業能力為基礎、以客戶需求為導向，不斷優化交易服務、深化金融科技應用、推動投資者教育與品牌建設，在變化的全球金融市場中持續探索長期價值。',
          '從香港出發，與全球市場同行。',
        ],
      },
    ],
    readMore: '閱讀全文',
    readLess: '收起全文',
  },
  cn: {
    lang: 'zh-Hans',
    eyebrow: '公司新闻',
    title: 'GLG2026年度荣膺「亚太黄金交易领军品牌」',
    date: '2026年9月22日',
    location: '香港',
    award: '亚太黄金交易领军品牌',
    summary:
      '金利宝环球有限公司于香港举行的全球金融产业博览会中获颁「亚太黄金交易领军品牌」奖项，肯定公司在贵金属交易服务、专业能力与品牌建设方面的持续投入。',
    introduction: [
      '2026年9月22日，在香港举行的全球金融产业博览会。来自金融交易、金融科技、资产管理及相关服务领域的众多行业机构、专家与专业人士齐聚现场，共同围绕全球金融市场发展趋势、金融科技创新与行业生态建设展开交流。',
      '金利宝环球有限公司（GOLD LABEL GLOBAL LIMITED）作为参展机构亮相本届博览会，并在大会颁奖环节荣获「亚太黄金交易领军品牌」奖项。',
      '此次参展与获奖，不仅是金利宝环球品牌发展进程中的重要节点，也代表着行业对金利宝在贵金属领域持续投入、专业服务及品牌建设成果的进一步关注与认可。',
    ],
    sections: [
      {
        heading: '荣誉加冕，见证金利宝贵金属领域专业实力',
        paragraphs: [
          '在金融市场不断走向全球化、专业化与科技化的背景下，投资者对于交易环境、服务能力、风险管理以及品牌专业性的要求持续提高。',
          '作为专注于贵金属交易服务的金融品牌，金利宝环球始终围绕贵金属市场持续深化自身专业能力，从交易服务、技术环境到客户体验与品牌建设，不断完善整体服务体系。',
          '此次获得「亚太黄金交易领军品牌」荣誉，是对金利宝环球在黄金及贵金属交易领域持续发展的阶段性认可。',
          '对于金利宝而言，奖项不仅是一份荣誉，更意味着责任。',
          '每一次行业认可背后，都离不开对于专业标准的长期坚持。面对不断变化的全球金融市场，金利宝将继续以稳健、专业和长期主义为核心，不断提升服务能力，以更加成熟的品牌形象面对市场与客户。',
        ],
      },
      {
        heading: '汇聚行业力量，链接全球金融生态',
        paragraphs: [
          '本届全球金融产业博览会汇聚众多金融机构、行业专家、渠道伙伴及专业投资者，为金融行业提供了一个交流趋势、连接资源与分享创新成果的平台。',
          '展会期间，金利宝环球通过品牌展位展示、现场交流及行业互动，与来自不同市场的金融从业者及合作伙伴进行了深入沟通。',
          '从行业趋势到市场需求，从金融科技发展到贵金属交易服务升级，现场交流让金利宝进一步了解全球金融市场变化，也为品牌未来连接更多行业资源、深化市场合作提供了新的契机。',
          '对于金利宝而言，参加行业展会并不仅仅是一次品牌展示。',
          '更重要的是，通过与市场、行业及合作伙伴持续交流，让品牌真正融入全球金融产业生态，在不断变化的市场环境中保持敏锐、开放与专业。',
        ],
      },
      {
        heading: '深耕贵金属交易，持续构建专业服务体系',
        paragraphs: [
          '金利宝环球有限公司立足香港，持续聚焦黄金、白银等贵金属交易领域。',
          '作为香港金银业贸易场金集团成员之一，金利宝不断完善自身贵金属业务体系，并围绕交易环境、技术支持、客户服务与市场教育等方向持续投入。',
          '依托香港国际金融中心的市场优势，金利宝希望进一步连接亚太及全球市场，为不同地区客户提供更加专业、高效的贵金属交易服务体验。',
          '与此同时，金利宝也在持续关注金融科技发展趋势，通过数字化工具、金融科技及智能化技术的应用探索，为未来业务发展构建更加完善的技术基础与服务能力。',
          '从产品与服务，到品牌与市场；从交易体验，到金融教育与行业交流，金利宝正在逐步构建更加完整的品牌生态。',
        ],
      },
      {
        heading: '载誉前行，让专业成为长期价值',
        paragraphs: [
          '荣誉，是一个阶段的认可，也是下一段旅程的开始。',
          '此次亮相全球金融产业博览会并荣获「亚太黄金交易领军品牌」，进一步坚定了金利宝持续深耕贵金属领域的发展方向。',
          '未来，金利宝环球将继续立足香港、连接全球，坚持以专业能力为基础，以客户需求为导向，不断优化交易服务、深化金融科技应用、推动投资者教育与品牌建设，在变化的全球金融市场中持续探索长期价值。',
          '从香港出发，与全球市场同行。',
        ],
      },
    ],
    readMore: '阅读全文',
    readLess: '收起全文',
  },
  en: {
    lang: 'en-GB',
    eyebrow: 'Company News',
    title: 'Gold Label Global named Asia-Pacific Leading Gold Trading Brand for 2026',
    date: '22 September 2026',
    location: 'Hong Kong',
    award: 'Asia-Pacific Leading Gold Trading Brand',
    summary:
      'Gold Label Global Limited received the “Asia-Pacific Leading Gold Trading Brand” award at the Global Financial Industry Expo in Hong Kong, recognising its continued investment in precious-metals trading services, professional capabilities and brand development.',
    introduction: [
      'On 22 September 2026, the Global Financial Industry Expo was held in Hong Kong. Institutions, experts and professionals from financial trading, financial technology, asset management and related services came together to discuss global market trends, fintech innovation and the development of the wider financial ecosystem.',
      'Gold Label Global Limited exhibited at the event and received the “Asia-Pacific Leading Gold Trading Brand” award during the ceremony.',
      'The exhibition and award mark an important stage in the development of the Gold Label Global brand. They also reflect further industry recognition of the company’s sustained investment, professional service and brand-building achievements in the precious-metals sector.',
    ],
    sections: [
      {
        heading: 'An award that reflects professional strength in precious metals',
        paragraphs: [
          'As financial markets become increasingly global, specialised and technology-driven, investors continue to raise their expectations for trading environments, service capabilities, risk management and professional standards.',
          'As a financial brand focused on precious-metals trading services, Gold Label Global continues to deepen its market expertise and strengthen its overall service framework across trading services, technology, client experience and brand development.',
          'The “Asia-Pacific Leading Gold Trading Brand” award is a milestone recognising the company’s continued development in gold and precious-metals trading. For Gold Label Global, the award is not only an honour but also a responsibility.',
          'Every form of industry recognition rests on a long-term commitment to professional standards. In a constantly changing global financial market, Gold Label Global will continue to place stability, professionalism and long-term thinking at the centre of its work while improving its service capabilities and presenting an increasingly mature brand to clients and the market.',
        ],
      },
      {
        heading: 'Bringing the industry together and connecting the global financial ecosystem',
        paragraphs: [
          'The Global Financial Industry Expo brought together financial institutions, industry experts, distribution partners and professional investors, providing a platform to discuss trends, connect resources and share innovation.',
          'Through its exhibition stand, on-site conversations and industry engagement, Gold Label Global held in-depth discussions with finance professionals and partners from different markets.',
          'These exchanges covered industry trends, market needs, fintech developments and upgrades to precious-metals trading services. They gave Gold Label Global a deeper view of changes in global financial markets and created new opportunities to connect with industry resources and strengthen future cooperation.',
          'For Gold Label Global, attending an industry exhibition is about more than presenting the brand. Ongoing dialogue with markets, industry participants and partners helps the company remain engaged with the global financial ecosystem while staying alert, open and professional in a changing environment.',
        ],
      },
      {
        heading: 'Deepening precious-metals expertise and strengthening professional services',
        paragraphs: [
          'Based in Hong Kong, Gold Label Global Limited remains focused on gold, silver and other precious-metals trading.',
          'As a member of the Gold Group of the Chinese Gold & Silver Exchange, the company continues to develop its precious-metals business and invest in its trading environment, technical support, client service and market education.',
          'Building on Hong Kong’s position as an international financial centre, Gold Label Global aims to strengthen its connections across Asia-Pacific and global markets while providing clients in different regions with a more professional and efficient precious-metals trading experience.',
          'The company also continues to follow developments in financial technology. It is exploring the use of digital tools, fintech and intelligent technologies to build stronger technical foundations and service capabilities for future growth.',
          'Across products and services, brand and markets, trading experience, financial education and industry dialogue, Gold Label Global is steadily building a more complete brand ecosystem.',
        ],
      },
      {
        heading: 'Moving forward with professional standards as a source of long-term value',
        paragraphs: [
          'An award recognises one stage of progress and marks the beginning of the next. Appearing at the Global Financial Industry Expo and receiving the “Asia-Pacific Leading Gold Trading Brand” award has reinforced Gold Label Global’s commitment to the precious-metals sector.',
          'Gold Label Global will continue to build from Hong Kong and connect with global markets. Guided by professional capabilities and client needs, the company will keep improving its trading services, deepening the application of financial technology, supporting investor education and developing its brand as it pursues long-term value in a changing global financial market.',
          'From Hong Kong to the global market.',
        ],
      },
    ],
    readMore: 'Read full release',
    readLess: 'Hide full release',
  },
  jp: japaneseAnnouncement,
  kr: koreanAnnouncement,
  th: thaiAnnouncement,
  vn: vietnameseAnnouncement,
  de: germanAnnouncement,
  fr: frenchAnnouncement,
  it: italianAnnouncement,
  es: spanishAnnouncement,
  ug: uyghurAnnouncement,
};
